// src/components/RH/Components/Tabs/EmpleadosTurnosTab.jsx
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Seguridad/Utils/Endpoints';
import { Models } from '../../../Seguridad/Utils/ModelsSeguridad';
import { ENDPOINTS as GeneralEndpoints } from '../../../General/Utils/Endpoints';
import { Models as GeneralModels } from '../../../RH/Utils/ModelsRelojChecador';
import AlertDialog from '../../../Lms/Components/Shared/AlertDialog';

const TITLETAB = 'Asignación de Turnos';

// Endpoints
const endpointAssigned = ENDPOINTS.empleadosTurnos;
const endpointAssignedById = ENDPOINTS.empleadosTurnosById;
const endpointAvailable = GeneralEndpoints.turnos;

// Models
const normalizeAssigned = Models.empleadosTurnos;
const normalizeAvailable = GeneralModels.turno;

// Keys
const ID_ASSIGNED_KEY = 'idEmpleadoTurno';
const ID_ITEM_KEY = 'idTurno';
const FK_USER_KEY = 'idEmpleado';
const ITEM_NAME_KEY = 'nombre';

export default function EmpleadosTurnosTab({ fkValue, onClose, onDirtyChange, onSaveSuccess }) {
  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

  // State for initial data (to calculate changes)
  const [initialAssigned, setInitialAssigned] = useState([]);

  // State for current local changes
  const [currentAssigned, setCurrentAssigned] = useState([]);

  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [resAvailable, resAssigned] = await Promise.all([
        axios.get(endpointAvailable),
        axios.get(endpointAssigned),
      ]);

      const items = (resAvailable?.data || []).map(normalizeAvailable);
      const rawAssigned = (resAssigned?.data || []).map(normalizeAssigned);

      // Helper para fecha local YYYY-MM-DD (Consistente con handleSave)
      const toDateStr = (d) => {
        if (!d || isNaN(d.getTime())) return 'Legacy';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };

      // Calcular PrÃ³ximo Lunes
      const today = new Date();
      const dayToday = today.getDay();
      const diff = dayToday === 0 ? 1 : (8 - dayToday);
      const nextMonday = new Date(today);
      nextMonday.setDate(today.getDate() + diff);
      const nextMondayStr = toDateStr(nextMonday);

      // 1. Filtrar por empleado
      const myAssignedAll = rawAssigned.filter(r => Number(r[FK_USER_KEY]) === Number(fkValue));

      let myAssignedSelected = [];

      if (myAssignedAll.length > 0) {
        // 2. Agrupar por fecha string (YYYY-MM-DD)
        const groups = {};

        myAssignedAll.forEach(item => {
          let key = 'Legacy';
          if (item.fechaAsignacion) {
            const dObj = new Date(item.fechaAsignacion);
            key = toDateStr(dObj);
          }
          if (!groups[key]) groups[key] = [];
          groups[key].push(item);
        });

        // 3. SelecciÃ³n Simplificada y Robusta:
        // Simplemente tomamos el Snapshot mÃ¡s reciente disponible en la lÃ­nea de tiempo.
        // Si hay registros futuros (Lunes), aparecerÃ¡n primero. Si no, aparecerÃ¡n los actuales.
        const keys = Object.keys(groups).sort().reverse();

        if (keys.length > 0) {
          // keys[0] es la fecha mÃ¡s grande (ej. "2026-01-12")
          myAssignedSelected = groups[keys[0]];
        }
      }

      // 4. DEDUPLICACIÃ“N VISUAL: 
      // Si la BD tiene duplicados para la misma fecha (por inserts anteriores), 
      // filtramos para mostrar solo una instancia por Turno.
      const uniqueMap = new Map();
      myAssignedSelected.forEach(item => {
        if (!uniqueMap.has(item[ID_ITEM_KEY])) {
          uniqueMap.set(item[ID_ITEM_KEY], item);
        }
      });
      myAssignedSelected = Array.from(uniqueMap.values());

      setAllItems(items);
      setInitialAssigned(myAssignedSelected);
      setCurrentAssigned(myAssignedSelected);
    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: `Error al cargar ${TITLETAB}`, variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fkValue) {
      load();
    }
  }, [fkValue]);

  // Calculate available items (All Items - Current Assigned Items)
  const availableItems = useMemo(() => {
    const assignedIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));
    return allItems.filter(r => !assignedIds.has(r[ID_ITEM_KEY]));
  }, [allItems, currentAssigned]);

  // Enriched assigned items (add name from catalog)
  const enrichedAssigned = useMemo(() => {
    return currentAssigned.map(assigned => {
      const itemDef = allItems.find(i => i[ID_ITEM_KEY] === assigned[ID_ITEM_KEY]);
      return {
        ...assigned,
        _displayName: itemDef ? (itemDef[ITEM_NAME_KEY] || itemDef.nombre) : `Item #${assigned[ID_ITEM_KEY]}`,
      };
    });
  }, [currentAssigned, allItems]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (initialAssigned.length !== currentAssigned.length) return true;

    const initialIds = new Set(initialAssigned.map(r => r[ID_ITEM_KEY]));
    const currentIds = new Set(currentAssigned.map(r => r[ID_ITEM_KEY]));

    for (let id of currentIds) {
      if (!initialIds.has(id)) return true;
    }
    return false;
  }, [initialAssigned, currentAssigned]);

  // Propagar estado "sucio"
  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(hasChanges);
    }
  }, [hasChanges, onDirtyChange]);

  // --- Drag and Drop Handlers ---

  const handleDragStart = (e, item, source) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetColumn) => {
    e.preventDefault();
    if (dragOverColumn !== targetColumn) {
      setDragOverColumn(targetColumn);
    }
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    setDragOverColumn(null);

    const dataStr = e.dataTransfer.getData('application/json');
    if (!dataStr) return;

    const { item, source } = JSON.parse(dataStr);

    if (source === targetColumn) return;

    if (targetColumn === 'assigned' && source === 'available') {
      addLocal(item);
    } else if (targetColumn === 'available' && source === 'assigned') {
      removeLocal(item);
    }
  };

  // --- Local State Modifiers ---

  const addLocal = (item) => {
    const exists = currentAssigned.some(r => r[ID_ITEM_KEY] === item[ID_ITEM_KEY]);
    if (exists) return;

    const newAssignment = {
      [ID_ASSIGNED_KEY]: 0, // Temporary ID
      [FK_USER_KEY]: Number(fkValue),
      [ID_ITEM_KEY]: item[ID_ITEM_KEY],
      fechaAsignacion: new Date().toISOString(), // Default date
    };

    setCurrentAssigned(prev => [...prev, newAssignment]);
  };

  const removeLocal = (assignedItem) => {
    setCurrentAssigned(prev => prev.filter(r => r[ID_ITEM_KEY] !== assignedItem[ID_ITEM_KEY]));
  };

  // --- Save / Cancel ---

  const handleCancel = () => {
    onClose?.();
    setCurrentAssigned(initialAssigned);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // ðŸ”¹ ESTRATEGIA DE VERSIONADO POR SNAPSHOT:
      // En lugar de calcular diferencias (add/remove), insertamos TODO el estado actual
      // como un nuevo set de registros para la prÃ³xima semana.

      // Calcular fecha del PrÃ³ximo Lunes para enviarla explÃ­citamente
      const today = new Date();
      const day = today.getDay(); // 0=Domingo, 1=Lunes...
      const diff = day === 0 ? 1 : (8 - day); // Domingo(+1), Lunes(+7), Martes(+6)...
      const nextMonday = new Date(today);
      nextMonday.setDate(today.getDate() + diff);
      nextMonday.setHours(0, 0, 0, 0);

      const fechaAsignacionStr = nextMonday.toISOString();

      // Helper para comparar fechas sin hora (YYYY-MM-DD local)
      const toDateStr = (d) => {
        if (!d || isNaN(d.getTime())) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };

      const nextMondayStr = toDateStr(nextMonday);
      // const toDateStr = (d) => {
      //   if (!d || isNaN(d.getTime())) return '';
      //   const y = d.getFullYear();
      //   const m = String(d.getMonth() + 1).padStart(2, '0');
      //   const day = String(d.getDate()).padStart(2, '0');
      //   return `${y}-${m}-${day}`;
      // };

      // const nextMondayStr = toDateStr(nextMonday);

      // ðŸ”¹ 1. Validar y LIMPIAR si ya existe un snapshot para ese Lunes Futuro
      // Consultamos de nuevo para asegurar consistencia
      const resCheck = await axios.get(endpointAssigned);
      const allRecords = (resCheck?.data || []).map(normalizeAssigned);

      console.log("--- DEBUG SAVE START ---");
      console.log("Next Monday Target:", nextMonday);
      const now = new Date();
      console.log("Comparison Date (Now):", now);

      console.log(`Checking ${allRecords.length} records...`);

      // Rango de la PRÃ“XIMA SEMANA: [nextMonday, nextMonday + 7 dÃ­as)
      const startOfNextWeek = new Date(nextMonday);
      startOfNextWeek.setHours(0, 0, 0, 0);
      const endOfNextWeek = new Date(startOfNextWeek);
      endOfNextWeek.setDate(endOfNextWeek.getDate() + 7);

      const recordsToDelete = allRecords.filter(r => {
        // Solo nos interesan los registros del empleado actual
        if (Number(r[FK_USER_KEY]) !== Number(fkValue)) return false;

        if (!r.fechaAsignacion) {
          console.log(`[Eval] ID:${r[ID_ASSIGNED_KEY]} Date:null -> skip (sin fechaAsignacion)`);
          return false;
        }

        const rDate = new Date(r.fechaAsignacion);
        if (isNaN(rDate.getTime())) {
          console.log(`[Eval] ID:${r[ID_ASSIGNED_KEY]} Date:${r.fechaAsignacion} -> skip (fecha invÃ¡lida)`);
          return false;
        }

        rDate.setHours(0, 0, 0, 0);
        const rDateStr = toDateStr(rDate);

        const inNextWeek = rDate >= startOfNextWeek && rDate < endOfNextWeek;
        console.log(`[Eval] ID:${r[ID_ASSIGNED_KEY]} Date:${r.fechaAsignacion} LocalDate:${rDateStr} InNextWeek?${inNextWeek}`);

        return inNextWeek;
      });

      console.log("Records to DELETE:", recordsToDelete.length, recordsToDelete);

      // Borrar registros futuros existentes para evitar duplicados
      if (recordsToDelete.length > 0) {
        console.log("Deleting...");
        const deletePromises = recordsToDelete.map(r => {
          const url = typeof endpointAssignedById === 'function'
            ? endpointAssignedById(r[ID_ASSIGNED_KEY])
            : `${endpointAssignedById}/${r[ID_ASSIGNED_KEY]}`;
          console.log("DELETE URL:", url);
          return axios.delete(url);
        });
        await Promise.all(deletePromises);
        console.log("Delete completed.");
      } else {
        console.log("Skipping Delete (No records found)");
      }

      // ðŸ”¹ 2. Insertar los nuevos registros
      const promises = [];

      for (const item of currentAssigned) {
        const payload = {
          [ID_ASSIGNED_KEY]: 0, // Siempre 0 para forzar inserciÃ³n nueva
          [FK_USER_KEY]: Number(fkValue),
          [ID_ITEM_KEY]: item[ID_ITEM_KEY],
          fechaAsignacion: fechaAsignacionStr
        };
        promises.push(axios.post(endpointAssigned, payload));
      }

      await Promise.all(promises);

      await load();
      if (onSaveSuccess) {
        onSaveSuccess();
      } else {
        setAlertDialog({ open: true, title: 'Éxito', message: <>Cambios guardados correctamente.<br/>El nuevo horario aplicará a partir del próximo lunes.</>, variant: 'success' });
      }

    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: 'Error al guardar los cambios.', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mini-crud">
      <div className="mini-topbar">
        <div>
          <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
          <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
            Arrastra o usa los botones (+ / x) para modificar. Guarda al finalizar.
          </div>
        </div>
      </div>

      <div className="dnd-container" style={{ maxHeight: '50vh', overflowY: 'auto', marginBottom: '1rem' }}>
        {/* Left: Available */}
        <div
          className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
          onDragOver={(e) => handleDragOver(e, 'available')}
          onDrop={(e) => handleDrop(e, 'available')}
        >
          <div className="dnd-header">Disponibles (Origen)</div>
          {loading && availableItems.length === 0 ? (
            <div className="dnd-empty">Cargando...</div>
          ) : availableItems.length === 0 ? (
            <div className="dnd-empty">No hay elementos disponibles</div>
          ) : (
            availableItems.map(item => (
              <div
                key={item[ID_ITEM_KEY]}
                className="dnd-item"
                draggable={!saving}
                onDragStart={(e) => handleDragStart(e, item, 'available')}
              >
                <span>{item[ITEM_NAME_KEY] || item.nombre || `Item #${item[ID_ITEM_KEY]}`}</span>
                <button
                  type="button"
                  onClick={() => addLocal(item)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.2em',
                    color: '#28a745',
                    cursor: 'pointer',
                    padding: '0 5px'
                  }}
                  title="Agregar"
                >
                  +
                </button>
              </div>
            ))
          )}
        </div>

        {/* Right: Assigned */}
        <div
          className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
          onDragOver={(e) => handleDragOver(e, 'assigned')}
          onDrop={(e) => handleDrop(e, 'assigned')}
        >
          <div className="dnd-header">Asignados (Destino)</div>
          {loading && enrichedAssigned.length === 0 ? (
            <div className="dnd-empty">Cargando...</div>
          ) : enrichedAssigned.length === 0 ? (
            <div className="dnd-empty">Arrastra aquí para asignar</div>
          ) : (
            enrichedAssigned.map(item => (
              <div
                key={item[ID_ITEM_KEY]}
                className="dnd-item"
                draggable={!saving}
                onDragStart={(e) => handleDragStart(e, item, 'assigned')}
              >
                <span>{item._displayName}</span>
                <button
                  type="button"
                  onClick={() => removeLocal(item)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.2em',
                    color: '#dc3545',
                    cursor: 'pointer',
                    padding: '0 5px'
                  }}
                  title="Remover"
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem' }}>
        <button
          className="roles-btn"
          onClick={handleCancel}
          type="button"
        >
          Cancelar / Reestablecer
        </button>
        <button
          className="btn-action"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{
            opacity: (!hasChanges || saving) ? 0.5 : 1,
            cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer'
          }}
          type="button"
        >
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>

      {alertDialog.open && (
        <AlertDialog
          open={alertDialog.open}
          onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
          title={alertDialog.title}
          message={alertDialog.message || ""}
          variant={alertDialog.variant || "info"}
        />
      )}


    </div>
  );
}

